// R2 (2026-08-27) — Client-side export helpers for Monitoring Working Order.
// exportWOsToExcel  → generate .xlsx via SheetJS
// printWOsToPDF     → open printable HTML window, trigger browser print → user
//                     Save as PDF (native flow, no extra library).
//
// Digunakan oleh WOMasterDetailView (per-chevron export + global export).
// Keep pure — no React, no toast; caller handles UX feedback.

import * as XLSX from "xlsx";
import { fmtDate, formatIDR, WO_STAGES } from "@/lib/constants";
import { deriveActionBucket, ACTION_BUCKET_META } from "@/lib/woStageGroup";
import { verifiedReceived, expectedNet, entryRetentionInfo } from "@/lib/paymentBreakdown";

// R3 (2026-08-27) — Column visibility mapping.
// Single source of truth: satu logical kolom (on-screen) ↔ satu/beberapa kolom output.
// Dipakai oleh WOMasterDetailView untuk sinkronkan pilihan kolom antara on-screen &
// export/print, sehingga PDF/Excel mengikuti pilihan user.
export const WO_EXPORT_COL_GROUPS = {
  po:         ["No. PO"],
  wo:         ["WO Number", "Judul"],
  outlet:     ["Outlet"],
  stage:      ["Tahap Saat Ini"],
  action:     ["Kategori WO"],
  activity:   ["Aktivitas Terakhir", "Tanggal Aktivitas"],
  wait_days:  ["Hari Menunggu"],
  target:     ["Tanggal Selesai Pekerjaan"],
};

function outletKey(p) {
  return (p.parent_project_name || p.project_name || "—").trim() || "—";
}

function stageLabel(p) {
  // 2026-06-20 (DUAL WORKFLOW): export membaca label tahap dari engine canonical
  // (__wf) — sumber yang sama dengan tabel on-screen; fallback stage lama.
  if (p.__wf?.chevronLabel) return p.__wf.chevronLabel;
  const s = WO_STAGES.find((x) => x.key === p.__stage);
  return s?.label || p.__stage || "—";
}

function bucketLabel(p) {
  // 2026-06-20 (aturan tri-state KETAT): true → "Urgent Case", false →
  // "Non-Urgent Case", selain itu → "Belum Diklasifikasikan". Jangan pakai
  // truthiness — false adalah nilai valid; jangan menebak dari data lain.
  const flag = p.is_urgent === true ? "Urgent Case" : p.is_urgent === false ? "Non-Urgent Case" : "Belum Diklasifikasikan";
  const manual = (p.perlu_tindakan_manual || "").trim();
  return manual ? `${flag} — ${manual}` : flag;
}

// WO terminal (Closed / Dibatalkan Owner) tidak lagi menghitung Hari Menunggu.
export function woWaitDays(p, la) {
  const terminal = ["closed", "cancelled_by_owner", "cancelled_owner"].includes(p?.__stage) || p?.status === "cancelled_by_owner";
  return terminal ? null : daysSince(la?.date);
}

function daysSince(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  } catch { return null; }
}

/**
 * Bangun array of row objects untuk tabel export.
 * @param {Array} wos - list WO (sudah __stage/__risk enriched)
 * @param {(p: any) => {label: string, date: string|null}} deriveLastActivity
 * @param {string[]=} visibleKeys - subset dari kolom logical (lihat WO_EXPORT_COL_GROUPS).
 *   Kalau undefined → semua kolom (backward-compatible dgn caller lama).
 */
export function buildExportRows(wos, deriveLastActivity, visibleKeys) {
  const allowedFields = visibleKeys
    ? new Set(visibleKeys.flatMap((k) => WO_EXPORT_COL_GROUPS[k] || []))
    : null;
  const pick = (obj) => {
    if (!allowedFields) return obj;
    const out = {};
    for (const k of Object.keys(obj)) if (allowedFields.has(k)) out[k] = obj[k];
    return out;
  };
  return wos.map((p) => {
    const la = deriveLastActivity(p);
    const target = p.project_done_date;
    return pick({
      "No. PO": p.po_number || "",
      "WO Number": p.wo_number || "",
      "Judul": p.wo_title || "",
      "Outlet": outletKey(p),
      "Tahap Saat Ini": stageLabel(p),
      "Kategori WO": bucketLabel(p),
      "Aktivitas Terakhir": la?.label || "",
      "Tanggal Aktivitas": la?.date ? fmtDate(la.date) : "",
      "Hari Menunggu": woWaitDays(p, la) ?? "",
      "Tanggal Selesai Pekerjaan": target ? fmtDate(target) : "",
    });
  });
}

/**
 * Download .xlsx dengan title sheet + auto column width.
 */
export function exportWOsToExcel(rows, { fileName = "wo-monitoring", sheetName = "WO Monitoring", title = "Monitoring Working Order" } = {}) {
  const wb = XLSX.utils.book_new();
  const header = rows.length > 0 ? Object.keys(rows[0]) : [
    "No. PO", "WO Number", "Judul", "Outlet", "Tahap Saat Ini", "Kategori WO",
    "Aktivitas Terakhir", "Tanggal Aktivitas", "Hari Menunggu", "Tanggal Selesai Pekerjaan",
  ];
  // Kompose aoa: [Title], [Meta], [], [Header], ...rows
  const aoa = [
    [title],
    [`Diekspor: ${new Date().toLocaleString("id-ID")}  ·  Total: ${rows.length} WO`],
    [],
    header,
    ...rows.map((r) => header.map((h) => r[h] ?? "")),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Merge title across all columns
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: header.length - 1 } },
  ];
  // Column widths — kira-kira dari max string length per kolom
  ws["!cols"] = header.map((h) => {
    const maxLen = Math.max(
      h.length,
      ...rows.map((r) => String(r[h] ?? "").length),
    );
    return { wch: Math.min(60, Math.max(10, maxLen + 2)) };
  });
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${fileName}_${stamp}.xlsx`);
}

/**
 * Open printable HTML window → user Save as PDF via browser print dialog.
 */
export function printWOsToPDF(rows, { title = "Monitoring Working Order", subtitle = "", moneyCols = [] } = {}) {
  const moneySet = new Set(moneyCols);
  const header = rows.length > 0 ? Object.keys(rows[0]) : [
    "No. PO", "WO Number", "Judul", "Outlet", "Tahap Saat Ini", "Kategori WO",
    "Aktivitas Terakhir", "Tanggal Aktivitas", "Hari Menunggu", "Tanggal Selesai Pekerjaan",
  ];
  const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const html = `<!doctype html>
<html lang="id">
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif; color: #0B1D3A; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h1 { font-size: 18px; margin: 0 0 4px; letter-spacing: -0.02em; }
  .meta { font-size: 11px; color: #64748b; margin-bottom: 12px; }
  /* 149-cw-r100 (2026-08-28) — Table border seragam:
     - border-collapse: collapse + explicit 1px border tiap cell → grid rapi
     - outer wrapper border 1.5px supaya sisi kanan/bawah tidak "hilang"
     - table-layout: fixed + word-wrap supaya cell tidak melebar melebihi lebar tabel */
  table { width: 100%; border-collapse: collapse; font-size: 10px; table-layout: fixed; border: 1.5px solid #475569; }
  /* 149-cw-r101 (2026-08-28) — Judul tabel (thead) diulang di setiap halaman
     PDF: display:table-header-group + explicit repeat properties. Row juga
     avoid break-inside supaya konten tidak terpotong. */
  thead { background: #0B1D3A; color: #fff; display: table-header-group; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  tfoot { display: table-footer-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  th { page-break-after: avoid; break-after: avoid; }
  th, td { border: 1px solid #475569; padding: 6px 8px; text-align: left; vertical-align: top; box-sizing: border-box; word-wrap: break-word; overflow-wrap: break-word; }
  th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }
  tr:nth-child(even) td { background: #f8fafc; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  @media print { .no-print { display: none; } }
</style>
</head>
<body>
  <h1>${esc(title)}</h1>
  <div class="meta">${esc(subtitle)}${subtitle ? " · " : ""}Dicetak: ${new Date().toLocaleString("id-ID")} · Total ${rows.length} WO</div>
  <table>
    <thead><tr><th style="width:34px;text-align:center;">No.</th>${header.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
    <tbody>
      ${rows.map((r, i) => `<tr><td class="num" style="text-align:center;">${i + 1}</td>${header.map((h) => {
        const v = r[h];
        // 2026-06-21: kolom numerik — uang diformat Rp, angka biasa ribuan.
        const isNum = typeof v === "number" || h === "Hari Menunggu";
        const txt = typeof v === "number"
          ? (moneySet.has(h) ? formatIDR(v) : v.toLocaleString("id-ID"))
          : v;
        return `<td class="${isNum ? "num" : ""}">${esc(txt)}</td>`;
      }).join("")}</tr>`).join("")}
    </tbody>
  </table>
  <div class="no-print" style="margin-top:16px; text-align:center;">
    <button onclick="window.print()" style="padding:8px 16px; background:#FF5A1F; color:#fff; border:none; font-weight:600; cursor:pointer;">Cetak / Simpan sebagai PDF</button>
  </div>
  <script>window.addEventListener("load", () => setTimeout(() => window.print(), 400));</script>
</body>
</html>`;
  const w = window.open("", "_blank");
  if (!w) {
    // Popup blocker; fallback ke download HTML
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}.html`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

// =============================================================================
// 2026-06-21 (user request): EXPORT DENGAN PILIHAN KOLOM.
// Katalog lengkap field WO yang bisa dichecklist user saat Export Excel /
// Print PDF — bukan hanya kolom tabel di layar. Termasuk SPH/Nego per
// pengajuan dan perhitungan keuangan (DPP, PPN, PPh, retensi, Kas Harus
// Diterima, dst) yang memakai rumus engine SAMA dengan halaman Keuangan
// (lib/paymentBreakdown.js). Urutan export: dikelompokkan per No. PO (PO sama
// berdekatan) lalu WO Number — via `sortForExport`.
// =============================================================================

const _N = (v) => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};
const _fd = (v) => (v ? fmtDate(v) : "");
const _r2 = (x) => Math.round(x * 100) / 100;

const _STATUS_LABEL = {
  active: "Aktif", completed: "Selesai", standby: "Standby",
  cancelled: "Dibatalkan", cancelled_by_owner: "Dibatalkan Owner",
};

const _isRetEntry = (e) =>
  Boolean(e?.is_retention_release || e?.is_retention_claim || (e?.retention_kind || "") === "release");

/** Agregasi keuangan satu WO dari income_entries — rumus identik engine
 *  payment_breakdown (snapshot net → expectedNet; retensi derivasi persen). */
function finOf(p) {
  let dpp = 0, ppn = 0, pph = 0, ret = 0, due = 0, rec = 0, over = 0;
  const entries = p.income_entries || [];
  for (const e of entries) {
    const r = verifiedReceived(e);
    rec += r;
    if (_isRetEntry(e)) continue;
    dpp += _N(e.amount || e.gross_invoice_amount || e.invoice_amount);
    ppn += _N(e.ppn_amount || e.vat_amount);
    pph += _N(e.pph_amount);
    const ri = entryRetentionInfo(e);
    if (ri.retType === "inside") ret += ri.ret;
    const cash = expectedNet(e);
    due += cash;
    over += Math.max(0, cash - r);
  }
  const exp = _N(p.total_expense) || (p.expense_entries || []).reduce((a, x) => a + _N(x.amount), 0);
  return {
    has: entries.length > 0, dpp: _r2(dpp), ppn: _r2(ppn), pph: _r2(pph),
    ret: _r2(ret), due: _r2(due), rec: _r2(rec), over: _r2(over),
    exp: _r2(exp), cashPos: _r2(rec - exp),
  };
}

const _m = (v, cond) => (cond ? v : ""); // kosongkan bila WO belum punya data terkait

/**
 * Katalog field export. `fmt: "money"` → PDF menampilkan Rp, Excel angka mentah.
 * `get(p, la)` — p = WO doc (sudah enriched __wf/__stage), la = deriveLastActivity(p).
 */
export const WO_EXPORT_FIELDS = [
  // ---- Identitas ----
  { key: "po_number", label: "No. PO", group: "Identitas", get: (p) => p.po_number || "" },
  { key: "po_date", label: "Tanggal PO", group: "Identitas", get: (p) => _fd(p.po_date) },
  { key: "wo_number", label: "WO Number", group: "Identitas", get: (p) => p.wo_number || "" },
  { key: "wo_title", label: "Judul", group: "Identitas", get: (p) => p.wo_title || p.project_name || "" },
  { key: "work_description", label: "Deskripsi Pekerjaan", group: "Identitas", get: (p) => p.work_description || "" },
  { key: "outlet", label: "Outlet", group: "Identitas", get: (p) => outletKey(p) },
  { key: "client", label: "Client", group: "Identitas", get: (p) => p.client || "" },
  { key: "wo_date", label: "Tanggal Terima WO", group: "Identitas", get: (p) => _fd(p.wo_date) },
  { key: "created_at", label: "Tanggal Dibuat", group: "Identitas", get: (p) => _fd(p.created_at) },
  // ---- Tahap & Waktu ----
  { key: "stage", label: "Tahap Saat Ini", group: "Tahap & Waktu", get: (p) => stageLabel(p) },
  { key: "category", label: "Kategori WO", group: "Tahap & Waktu", get: (p) => bucketLabel(p) },
  { key: "last_activity", label: "Aktivitas Terakhir", group: "Tahap & Waktu", get: (p, la) => la?.label || "" },
  { key: "last_activity_date", label: "Tanggal Aktivitas", group: "Tahap & Waktu", get: (p, la) => (la?.date ? fmtDate(la.date) : "") },
  { key: "wait_days", label: "Hari Menunggu", group: "Tahap & Waktu", fmt: "int", get: (p, la) => woWaitDays(p, la) ?? "" },
  { key: "site_survey_date", label: "Site Survey", group: "Tahap & Waktu", get: (p) => _fd(p.site_survey_date) },
  { key: "done_date", label: "Tanggal Selesai Pekerjaan", group: "Tahap & Waktu", get: (p) => _fd(p.project_done_date) },
  { key: "invoice_sent_date", label: "Invoice Terkirim", group: "Tahap & Waktu", get: (p) => _fd(p.invoice_sent_date) },
  { key: "status_label", label: "Status", group: "Tahap & Waktu", get: (p) => _STATUS_LABEL[p.status] || p.status || "" },
  // ---- Status invoice kanonis (backend compute_invoice_origin_status) ----
  { key: "inv_official_status", label: "Status Invoice Resmi", group: "Status Invoice", get: (p) => p.payment_breakdown?.invoice_status?.official_label || "" },
  { key: "inv_legacy_status", label: "Status Catatan Legacy (saldo historis)", group: "Status Invoice", get: (p) => p.payment_breakdown?.invoice_status?.legacy_label || "Tidak ada" },
  { key: "inv_receipt_status", label: "Status Klasifikasi Penerimaan", group: "Status Invoice", get: (p) => { const s = p.payment_breakdown?.invoice_status; return s ? [s.receipt_label || "Tidak ada penerimaan", s.reconciliation_badge].filter(Boolean).join(" · ") : ""; } },
  // ---- SPH & Negosiasi ----
  { key: "sph_count", label: "Jumlah SPH/Nego", group: "SPH & Negosiasi", fmt: "int", get: (p) => (p.sph_submissions || []).length },
  // ---- Keuangan ----
  { key: "contract_value", label: "Nilai Kontrak", group: "Keuangan", fmt: "money", get: (p) => _r2(_N(p.contract_value)) },
  { key: "po_value", label: "Nilai PO", group: "Keuangan", fmt: "money", get: (p) => _m(_r2(_N(p.po_value)), p.po_value != null && p.po_value !== "") },
  { key: "dpp", label: "DPP (Σ Invoice)", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.dpp, f.has); } },
  { key: "ppn", label: "PPN", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.ppn, f.has); } },
  { key: "pph", label: "PPh", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.pph, f.has); } },
  { key: "retention_pct", label: "Retensi (%)", group: "Keuangan", get: (p) => (p.retention_pct ? `${p.retention_pct}%` : "") },
  { key: "retention_amount", label: "Nilai Retensi", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.ret, f.has); } },
  { key: "cash_due", label: "Kas Harus Diterima", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.due, f.has); } },
  { key: "received", label: "Sudah Diterima", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.rec, f.has); } },
  { key: "overdue", label: "Sisa Piutang", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.over, f.has); } },
  { key: "expense", label: "Total Pengeluaran", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.exp, f.has || f.exp > 0); } },
  { key: "cash_position", label: "Posisi Kas", group: "Keuangan", fmt: "money", get: (p) => { const f = finOf(p); return _m(f.cashPos, f.has || f.exp > 0); } },
  // ---- Lainnya ----
  { key: "pic", label: "PIC / Dibuat Oleh", group: "Lainnya", get: (p) => p.pic_name || p.created_by_name || "" },
  { key: "updated_at", label: "Update Terakhir", group: "Lainnya", get: (p) => _fd(p.updated_at) },
];

/** Kolom bawaan = 10 kolom tabel saat ini (user menambah sesuai kebutuhan). */
export const WO_EXPORT_DEFAULT_KEYS = [
  "po_number", "wo_number", "wo_title", "outlet", "stage", "category",
  "last_activity", "last_activity_date", "wait_days", "done_date",
];

/** Jumlah pengajuan SPH/Nego terbanyak di dataset (cap 8) — menentukan berapa
 *  pasang kolom "SPH/Nego #i" yang ditawarkan/dihasilkan. */
export function sphColumnCount(wos) {
  if (!wos || !wos.length) return 0;
  return Math.min(8, Math.max(0, ...wos.map((p) => (p.sph_submissions || []).length)));
}

/** Opsi checklist SPH per pengajuan (satu key memilih 2 kolom: Tanggal & Catatan). */
export function sphFieldOptions(maxSph) {
  const out = [];
  for (let i = 1; i <= (maxSph || 0); i++) {
    out.push({ key: `sph_${i}`, label: `SPH/Nego #${i} (Tanggal & Catatan)`, group: "SPH & Negosiasi" });
  }
  return out;
}

/** Urutan export: kelompok per No. PO (PO sama berdekatan) → WO Number →
 *  tanggal WO. Hanya untuk output export; urutan tabel di layar tidak berubah. */
export function sortForExport(wos) {
  return [...(wos || [])].sort((a, b) => {
    const pa = String(a.po_number || "￿"), pb = String(b.po_number || "￿");
    if (pa !== pb) return pa.localeCompare(pb, "id");
    const wa = String(a.wo_number || ""), wb = String(b.wo_number || "");
    if (wa !== wb) return wa.localeCompare(wb, "id");
    return String(a.wo_date || "").localeCompare(String(b.wo_date || ""));
  });
}

/**
 * Bangun rows export dari pilihan kolom user.
 * @returns {{ rows: object[], moneyHeaders: string[] }} — moneyHeaders dipakai
 *   printWOsToPDF utk format Rp; Excel menulis angka mentah (bisa dijumlah).
 */
export function buildExportRowsV2(wos, fieldKeys, deriveLastActivity) {
  const keySet = new Set(fieldKeys && fieldKeys.length ? fieldKeys : WO_EXPORT_DEFAULT_KEYS);
  const maxSph = sphColumnCount(wos);
  const cols = [];
  for (const f of WO_EXPORT_FIELDS) {
    if (!keySet.has(f.key)) continue;
    cols.push({ label: f.label, fmt: f.fmt || null, get: f.get });
    if (f.key === "sph_count") {
      // Kolom per pengajuan tepat setelah kolom jumlah (mengikuti urutan katalog).
      for (let i = 1; i <= maxSph; i++) {
        if (!keySet.has(`sph_${i}`)) continue;
        const idx = i - 1;
        cols.push({ label: `SPH/Nego #${i} Tanggal`, fmt: null, get: (p) => _fd((p.sph_submissions || [])[idx]?.date) });
        cols.push({
          label: `SPH/Nego #${i} Catatan`, fmt: null,
          get: (p) => {
            const s = (p.sph_submissions || [])[idx];
            if (!s) return "";
            return [s.status, s.note].filter(Boolean).join(" — ");
          },
        });
      }
    }
  }
  const rows = (wos || []).map((p) => {
    const la = deriveLastActivity ? deriveLastActivity(p) : null;
    const out = {};
    for (const c of cols) out[c.label] = c.get(p, la);
    return out;
  });
  const moneyHeaders = cols.filter((c) => c.fmt === "money").map((c) => c.label);
  return { rows, moneyHeaders };
}
